import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Carousel from 'LuxKit/Carousel/Carousel'
import FilterChip from 'LuxKit/Chips/FilterChip'
import ExperienceSearchLandmarksToggle, { type ExperienceLandmarksToggleState } from './ExperienceSearchLandmarksToggle'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryDown } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useMostPopulatedSearchExperienceFacets, {
  type CategoryData,
} from '../hooks/useMostPopulatedSearchExperienceFacets'
import { arrayToObject } from 'lib/array/arrayUtils'
import { sortCategoriesByRelevance } from 'lib/experiences/categoryRelevanceRanking'
import {
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
} from 'constants/experience'
import {
  EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID,
  EXP_CATEGORY_TRANSFERS_SEARCH_ID,
  getNextCategoryCodesForSearchCategory,
  isTravelEssentialsSearchCategoryId,
  splitTravelEssentialsCategoryGroups,
} from './experienceSearchTravelEssentialsSplit'
import useExperiment from 'hooks/Experiments/useExperiment'
import { Experiments } from 'constants/experiments'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import { getSearchThemeTitle } from '../hooks/useExperienceThemes'

const EXCLUDED_QUICK_FILTER_CATEGORY_IDS = [
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
]

const CategoryChipWrapper = styled.div`
  display: inline-flex;
  position: relative;
`

const CategoryLabelChip = styled(FilterChip)`
  padding-inline-end: ${rem(32)};

  ${mediaQueryDown.tablet} {
    padding-inline-end: ${rem(12)};
  }
`

const CategoryExpandButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  inset-block: 0;
  inset-inline-end: 0;
  justify-content: center;
  padding: 0;
  position: absolute;
  width: ${rem(32)};

  > svg {
    height: ${rem(16)};
    width: ${rem(16)};
  }

  ${mediaQueryDown.tablet} {
    > svg {
      visibility: hidden;
    }
  }
`

interface CategoryFilterChipProps {
  categoryData: CategoryData;
  categoryFacetsById: Record<number, App.ExperienceCategoryFacet>;
  selectedCategoryCodes: Array<number>;
  onApply: (
    parentId: number,
    childIds: Array<number>,
    selectedIds: Set<number>,
  ) => void;
  onReset: (parentId: number, childIds: Array<number>) => void;
  isMobile: boolean;
  isCompactViewport: boolean;
}

function CategoryFilterChip({
  categoryData,
  categoryFacetsById,
  selectedCategoryCodes,
  onApply,
  onReset,
  isMobile,
  isCompactViewport,
}: CategoryFilterChipProps) {
  const intl = useIntl()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const [isOpen, toggleOpen, , closeDropdown] = useToggle()
  const chipRef = useRef<HTMLButtonElement>(null)
  const expandButtonRef = useRef<HTMLButtonElement>(null)
  const chipWrapperRef = useRef<HTMLDivElement>(null)

  const childCategories = categoryData.children
  const parentId = categoryData.parent.id
  const categoryName = getSearchThemeTitle(intl, parentId, categoryData.parent.name)
  const childIds = useMemo(
    () => childCategories.map((c) => c.id),
    [childCategories],
  )

  const allRelatedIds = useMemo(
    () => [parentId, ...childIds],
    [parentId, childIds],
  )
  const hasSelection = allRelatedIds.some((id) =>
    selectedCategoryCodes.includes(id),
  )

  const startIcon = useMemo(() => {
    if (parentId === EXP_CATEGORY_TRANSFERS_SEARCH_ID) {
      return <LineCarIcon />
    }
    if (parentId === EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID) {
      return <LinePlaneDepartureIcon />
    }
    return undefined
  }, [parentId])

  const [pendingChecked, setPendingChecked] = useState<Set<number>>(() => {
    const isParentSelected = selectedCategoryCodes.includes(parentId)
    if (isParentSelected) {
      return new Set(childIds)
    }
    return new Set(childIds.filter((id) => selectedCategoryCodes.includes(id)))
  })

  useEffect(() => {
    if (isOpen) {
      const isParentSelected = selectedCategoryCodes.includes(parentId)
      if (isParentSelected) {
        setPendingChecked(new Set(childIds))
      } else {
        setPendingChecked(
          new Set(childIds.filter((id) => selectedCategoryCodes.includes(id))),
        )
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleToggle = useCallback((value: number) => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const visibleChildren = useMemo(
    () =>
      childCategories.filter((c) => (categoryFacetsById[c.id]?.count ?? 0) > 0),
    [childCategories, categoryFacetsById],
  )

  const visibleChildIds = useMemo(
    () => visibleChildren.map((child) => child.id),
    [visibleChildren],
  )

  const currentCategorySelectionComplete = selectedCategoryCodes.includes(parentId) ||
    (visibleChildIds.length > 0 && visibleChildIds.every((id) => selectedCategoryCodes.includes(id)))

  const handleChipClick = useCallback(() => {
    if (currentCategorySelectionComplete) {
      trackSearchFilter({
        label: categoryName,
        type: 'category',
        value: 'cleared',
      })
      onReset(parentId, childIds)
      closeDropdown()
      return
    }

    const selectedIds = isTravelEssentialsSearchCategoryId(parentId) ?
      visibleChildIds :
        [parentId]

    trackSearchFilter({
      label: categoryName,
      type: 'category',
      value: 'selected',
    })
    onApply(parentId, childIds, new Set(selectedIds))
    closeDropdown()
  }, [categoryName, currentCategorySelectionComplete, trackSearchFilter, onReset, parentId, childIds, closeDropdown, visibleChildIds, onApply])

  const handleExpandClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    toggleOpen()
  }, [toggleOpen])

  const handleApply = useCallback(() => {
    const filterContexts = childCategories
      .filter((child) => {
        const wasSelected = selectedCategoryCodes.includes(parentId) ||
          selectedCategoryCodes.includes(child.id)
        return wasSelected !== pendingChecked.has(child.id)
      })
      .map((child) => ({
        label: child.name,
        type: 'category' as const,
        value: pendingChecked.has(child.id) ? 'selected' : 'unselected',
      }))
    trackSearchFilter(filterContexts)
    onApply(parentId, childIds, pendingChecked)
    closeDropdown()
  }, [childCategories, selectedCategoryCodes, parentId, pendingChecked, trackSearchFilter, onApply, childIds, closeDropdown])

  const handleReset = useCallback(() => {
    if (hasSelection) {
      trackSearchFilter({
        label: categoryName,
        type: 'category',
        value: 'cleared',
      })
    }
    onReset(parentId, childIds)
    closeDropdown()
  }, [categoryName, hasSelection, trackSearchFilter, onReset, parentId, childIds, closeDropdown])

  const applyText = intl.formatMessage({
    id: 'XGAxBfjicX0x40wpm7Xt',
    defaultMessage: 'Apply',
    description:
      'Button text to apply sort and filter selections in experience search',
  })

  const resetText = intl.formatMessage({
    id: '1BCuuKA_YbmI7wo_U3rn',
    defaultMessage: 'Reset',
    description:
      'Link to reset filter selections in the search or listing filters panel',
  })
  const openOptionsLabel = intl.formatMessage(
    {
      id: 'gaA2CCb8QTcN6-SId-bG',
      defaultMessage: 'Open {categoryName} options',
      description:
        'Accessibility label for the button that opens experience category filter options',
    },
    { categoryName },
  )

  const allSelected = useMemo(
    () =>
      visibleChildren.length > 0 &&
      visibleChildren.every((c) => pendingChecked.has(c.id)),
    [visibleChildren, pendingChecked],
  )

  const handleSelectAll = useCallback(() => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (visibleChildren.every((c) => next.has(c.id))) {
        visibleChildren.forEach((c) => next.delete(c.id))
      } else {
        visibleChildren.forEach((c) => next.add(c.id))
      }
      return next
    })
  }, [visibleChildren])

  const checkboxList = (
    <>
      {visibleChildren.length > 0 && (
        <CheckboxInputListItem
          inputPlacement="start"
          checked={allSelected}
          onChange={handleSelectAll}
          title={intl.formatMessage({
            id: 'VTp-nGNzNfgb6HumlrZx',
            defaultMessage: 'Select all',
            description:
              'Checkbox to select all subcategory options in a category filter dropdown',
          })}
          size="medium"
        />
      )}
      {visibleChildren.map((child) => {
        const count = categoryFacetsById[child.id]?.count ?? 0
        return (
          <CheckboxInputListItem
            key={child.id}
            inputPlacement="start"
            checked={pendingChecked.has(child.id)}
            onChange={() => handleToggle(child.id)}
            title={child.name}
            size="medium"
          >
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'CV8utsl6cVxsfdk50i1a',
                  defaultMessage: '({count})',
                  description:
                    'Count of available items shown next to a category filter option',
                },
                { count },
              )}
            </BodyText>
          </CheckboxInputListItem>
        )
      })}
    </>
  )

  return (
    <div>
      <CategoryChipWrapper ref={chipWrapperRef}>
        <CategoryLabelChip
          ref={chipRef}
          size="medium"
          selected={hasSelection}
          onClick={handleChipClick}
          startIcon={startIcon}
          endIcon={isCompactViewport ? (isOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />) : undefined}
        >
          {categoryName}
        </CategoryLabelChip>
        <CategoryExpandButton
          aria-label={openOptionsLabel}
          onClick={handleExpandClick}
          ref={expandButtonRef}
          type="button"
        >
          {isOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
        </CategoryExpandButton>
      </CategoryChipWrapper>
      <CSSBreakpoint min="tablet">
        <DropdownList
          size="S"
          width="max"
          triggerRef={[chipRef, expandButtonRef]}
          anchorRef={chipWrapperRef}
          open={isOpen}
          placement="bottom-start"
          onClose={closeDropdown}
          title={categoryName}
          hasDismissButton
          primaryActionProps={{
            children: applyText,
            onClick: handleApply,
            style: { flex: 1 },
          }}
          secondaryActionProps={{
            kind: 'tertiary',
            children: resetText,
            onClick: handleReset,
          }}
        >
          {checkboxList}
        </DropdownList>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <ModalBase isOpen={isOpen && isMobile} onClose={closeDropdown}>
          <ModalHeader
            title={categoryName}
            onCloseButtonClick={closeDropdown}
          />
          <ModalBody>
            <ModalContent flushListItems>{checkboxList}</ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: applyText,
              onClick: handleApply,
              style: { flexGrow: 2 },
            }}
            secondaryActionProps={{
              children: resetText,
              onClick: handleReset,
              style: { flexGrow: 0 },
            }}
          />
        </ModalBase>
      </CSSBreakpoint>
    </div>
  )
}

interface Props {
  toggleFilterPanel: () => void;
  place?: App.Place;
  landmarksToggle?: ExperienceLandmarksToggleState;
}

function ExperienceExposedFiltersHeader({ toggleFilterPanel, place, landmarksToggle }: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const isTablet = useScreenSizeOnly('tablet')
  const isCompactViewport = isMobile || isTablet
  const { isAnyVariant: discoveryRevampEnabled } = useExperiment(
    Experiments.experiencesSearchDiscoveryRevamp,
  )
  const { searchFilters, setCategoryCodesSearchFilter } =
    useExperiencesSearchFilters()
  const { primaryCategories, categories: categoryFacets } =
    useMostPopulatedSearchExperienceFacets(place, {
      rankCategoriesByRelevance: discoveryRevampEnabled,
      prioritiseSelectedCategories: discoveryRevampEnabled,
      sendPerformanceVariant: discoveryRevampEnabled,
    })

  const categoryFacetsById = useMemo(
    () => arrayToObject(categoryFacets, (f) => f.categoryId),
    [categoryFacets],
  )

  const selectedCategoryCodes = useMemo(
    () => searchFilters.categoryCodes ?? [],
    [searchFilters.categoryCodes],
  )

  const travelEssentialsSplitLabels = useMemo(() => ({
    transfers: intl.formatMessage({
      id: 'WGj8M709yLcgGiD9hdij',
      defaultMessage: 'Transfers',
      description: 'Experience search category label for transfer-related travel essentials',
    }),
    loungesAndServices: intl.formatMessage({
      id: 'x8JnpWwJHaV5vW0mX-B0',
      defaultMessage: 'Lounges and Services',
      description: 'Experience search category label for lounge and airport service travel essentials',
    }),
  }), [intl])

  const topLevelCategories = useMemo(() => {
    const eligible = primaryCategories.filter(
      (c) => !EXCLUDED_QUICK_FILTER_CATEGORY_IDS.includes(c.parent.id),
    )
    const categoryFacetsById = arrayToObject(categoryFacets, (f) => f.categoryId)
    let displayCategories = eligible

    // Search-page A/B only: present virtual Transfers/Lounges groups while
    // retaining the real Travel Essentials category IDs for filtering.
    if (discoveryRevampEnabled) {
      displayCategories = splitTravelEssentialsCategoryGroups(
        eligible,
        travelEssentialsSplitLabels,
        categoryFacetsById,
      )
    }

    return sortCategoriesByRelevance(
      place,
      displayCategories.map((categoryData) => ({
        ...categoryData,
        categoryId: categoryData.parent.id,
        name: getSearchThemeTitle(intl, categoryData.parent.id, categoryData.parent.name),
        relevanceScore: categoryData.relevanceScore,
        relatedCategoryIds: [
          categoryData.parent.id,
          ...categoryData.children.map((child) => child.id),
        ],
      })),
      undefined,
      {
        selectedCategoryIds: discoveryRevampEnabled ? selectedCategoryCodes : [],
        useRelevanceRanking: discoveryRevampEnabled,
      },
    )
  }, [place, discoveryRevampEnabled, primaryCategories, categoryFacets, selectedCategoryCodes, travelEssentialsSplitLabels, intl])

  const onCategoryApply = useCallback(
    (parentId: number, childIds: Array<number>, selectedIds: Set<number>) => {
      const current = searchFilters.categoryCodes ?? []
      setCategoryCodesSearchFilter(getNextCategoryCodesForSearchCategory(
        current,
        parentId,
        childIds,
        Array.from(selectedIds),
      ))
    },
    [searchFilters.categoryCodes, setCategoryCodesSearchFilter],
  )

  const onCategoryReset = useCallback(
    (parentId: number, childIds: Array<number>) => {
      const current = searchFilters.categoryCodes ?? []
      setCategoryCodesSearchFilter(getNextCategoryCodesForSearchCategory(
        current,
        parentId,
        childIds,
        [],
      ))
    },
    [searchFilters.categoryCodes, setCategoryCodesSearchFilter],
  )

  const allFiltersLabel = intl.formatMessage({
    id: '-lpIiE7WOtNg4S1Miji_',
    defaultMessage: 'Sort & Filters',
    description:
      'Label for the sort and filters chip in experience search exposed filters',
  })

  // Landmarks opt-in toggle, pinned as the first chip (left of the category chips) per the
  // 2026-06-17 product decision.
  const landmarksChip = landmarksToggle?.show ? (
    <ExperienceSearchLandmarksToggle
      active={landmarksToggle.active}
      onToggle={landmarksToggle.onToggle}
    />
  ) : undefined

  return (
    <Carousel gap={8} gutterStyle="overflow" gutterSize={16}>
      <FilterChip
        size="medium"
        startIcon={<LineFilterAltIcon />}
        onClick={toggleFilterPanel}
      >
        {allFiltersLabel}
      </FilterChip>

      {landmarksChip}

      {topLevelCategories.map((cat) => (
        <CategoryFilterChip
          key={cat.parent.id}
          categoryData={cat}
          categoryFacetsById={categoryFacetsById}
          selectedCategoryCodes={selectedCategoryCodes}
          onApply={onCategoryApply}
          onReset={onCategoryReset}
          isMobile={isMobile}
          isCompactViewport={isCompactViewport}
        />
      ))}
    </Carousel>
  )
}

export default ExperienceExposedFiltersHeader
